import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../../../services/apiService';
import { money } from '../../../utils/money';
import {
  Btn, Chip, DangerBtn, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, TextInput, fmtDate, panelGet, panelPost,
  useAdminGet,
} from '../kit';
import { Fact, Pager, Pill, S, TEAM_INBOX, type Tone, num, pageInfo, rowsOf, saveBlob, str, useDebounced } from './shared';

/**
 * IRCC registrations — the "Register interest" form on
 * /ip-intel-report/iit-bombay (IIT Bombay faculty, IRCC pays half; nothing is
 * paid on the site). Backed by Backend/ip_intel_programme.py, merged into the
 * same panel section:
 *   GET  ip_intel/registrations {q, status, payment, from, to, page, per_page}
 *        → rows (with payment_status, expected/received) + KPIs + payments_summary + programme
 *   GET  ip_intel/registration {id} → the registration with its payment entries
 *   GET  ip_intel/registration_tech_search {q} → Cosmos listings (the intake form's lookup)
 *   POST ip_intel/programme_save · registration_update · registration_resend · registration_tech_update
 *   POST ip_intel/registration_payment_add · registration_payment_delete · registration_payment_status
 * Payments are recorded by hand (IRCC collects the share offline); nothing is
 * e-mailed when one is recorded. The export is a raw admin route (one row per
 * technology).
 */

const PROGRAMME = 'iitb_ircc';
const PAGE_PATH = '/ip-intel-report/iit-bombay';
const EXPORT_XLSX = '/api/v1/admin/ip-intel/registrations/export.xlsx';
const EXPORT_CSV = '/api/v1/admin/ip-intel/registrations/export.csv';

export const REG_STATUSES = ['new', 'contacted', 'payment_received', 'converted', 'cancelled'] as const;
const REG_META: Record<string, { label: string; tone: Tone; hint: string }> = {
  new: { label: 'New', tone: 'warn', hint: 'Just registered; IRCC has not been in touch yet' },
  contacted: { label: 'Contacted by IRCC', tone: 'info', hint: 'IRCC has sent the payment details' },
  payment_received: { label: 'Payment received', tone: 'good', hint: 'Older status: payments are now recorded under Payment below' },
  converted: { label: 'Converted to orders', tone: 'good', hint: 'The reports are in the Orders tab' },
  cancelled: { label: 'Cancelled', tone: 'neutral', hint: 'Withdrawn; its places are free again' },
};

const RegPill: React.FC<{ status: any }> = ({ status }) => {
  const m = REG_META[str(status)];
  return <Pill tone={m?.tone || 'neutral'} title={m?.hint}>{m?.label || str(status) || '—'}</Pill>;
};

const MailPill: React.FC<{ status: any; error?: any; what: string }> = ({ status, error, what }) => {
  const s = str(status);
  if (s === 'sent') return <Pill tone="good" title={`${what}: sent`}>Sent</Pill>;
  if (s === 'failed') return <Pill tone="bad" title={str(error) || `${what}: did not go out`}>Failed</Pill>;
  return <Pill tone="neutral" title={what}>{s || '—'}</Pill>;
};

// ---------------------------------------------------------------------------
// Payments — recorded by hand, a ledger per registration.
// ---------------------------------------------------------------------------
export const PAY_STATUSES = ['pending', 'partial', 'received', 'waived'] as const;
type PayStatus = typeof PAY_STATUSES[number];
const PAY_META: Record<string, { label: string; tone: Tone; hint: string }> = {
  pending: { label: 'Pending', tone: 'warn', hint: 'Nothing recorded yet' },
  partial: { label: 'Part-paid', tone: 'info', hint: 'Some of the share is in' },
  received: { label: 'Received', tone: 'good', hint: 'The share is in' },
  waived: { label: 'Waived', tone: 'neutral', hint: 'Nothing will be collected' },
};
const PAY_FILTERS = ['', 'unpaid', 'pending', 'partial', 'received', 'waived'];
const PAY_FILTER_LABELS: Record<string, string> = {
  '': 'Any payment', unpaid: 'Unpaid (pending or part-paid)', pending: 'Pending', partial: 'Part-paid',
  received: 'Received', waived: 'Waived',
};
const METHODS = ['IRCC', 'Bank transfer', 'UPI', 'Cheque', 'Other'];
/** Mirror ip_intel_programme.PAYMENT_AMOUNT_MAX (₹1 crore, here in paise),
 *  PAYMENT_EARLIEST, REFERENCE_MAX and PAYMENT_NOTE_MAX; the server re-checks. */
const AMOUNT_MAX_MINOR = 1_00_00_000 * 100;
const EARLIEST_DAY = '2020-01-01';
const REFERENCE_MAX = 120;
const PAY_NOTE_MAX = 1000;

/** A cancelled registration with nothing paid owes nothing: "Not due" reads truer than "Pending". */
const PayPill: React.FC<{ row: any }> = ({ row }) => {
  const s = str(row?.payment_status) || 'pending';
  if (str(row?.status) === 'cancelled' && s === 'pending' && !num(row?.received_minor)) {
    return <Pill tone="neutral" title="Cancelled: nothing is expected">Not due</Pill>;
  }
  const m = PAY_META[s];
  return <Pill tone={m?.tone || 'neutral'} title={m?.hint}>{m?.label || s}</Pill>;
};

const inr = (minor: any, currency?: any) => money(num(minor), str(currency) || 'INR');

/** "20,000" / "₹23,600.50" / "20000" → paise; NaN when it is not an amount. */
const parseAmount = (v: string): number => {
  const s = v.replace(/[₹,\s]/g, '').replace(/^(rs\.?|inr)/i, '');
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return NaN;
  return Math.round(Number(s) * 100);
};

const PAY_KEYS = ['payments', 'payment_status', 'payment_note', 'payment_updated_at', 'payment_updated_by',
  'expected_minor', 'expected_with_tax_minor', 'received_minor', 'received_ex_tax_minor', 'outstanding_minor',
  'outstanding_with_tax_minor', 'tax_rate_pct', 'last_received_on', 'payments_count'];

/** Rounding slack for "received in full" (₹1), as the server's PAID_SLACK_MINOR. */
const PAID_SLACK_MINOR = 100;
/** An amount ticked "includes GST", back to before tax (₹23,600 at 18% → ₹20,000), as the server's _ex_tax. */
const exTax = (minor: number, includesTax: boolean, rate: number) =>
  includesTax ? Math.round(minor * 100 / (100 + rate)) : minor;

/** The registration out of a GET registration / payment-action payload,
 *  whichever way it is nested ({row, payments…}, {registration: {row…}} or
 *  the bare row); null when there is none. */
const regOf = (data: any): any | null => {
  const outer = data?.registration ?? data;
  if (!outer || typeof outer !== 'object') return null;
  const row = outer.row && typeof outer.row === 'object' ? outer.row : outer;
  if (row.id === undefined || row.id === null) return null;
  const merged: any = { ...row };
  PAY_KEYS.forEach(k => { if (merged[k] === undefined && outer[k] !== undefined) merged[k] = outer[k]; });
  if (merged.tax_rate_pct === undefined && outer.programme?.tax_rate_pct !== undefined) {
    merged.tax_rate_pct = outer.programme.tax_rate_pct;
  }
  return merged;
};

const TRLS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

/** closes_at (ISO) → the YYYY-MM-DD it falls on in India, for a date input. */
const istDay = (iso: any): string => {
  const d = new Date(str(iso));
  if (!str(iso) || isNaN(d.getTime())) return '';
  try { return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }); } catch { return d.toISOString().slice(0, 10); }
};
const todayIst = () => istDay(new Date().toISOString());
const dayLabel = (iso: any): string => {
  const d = new Date(str(iso));
  if (!str(iso) || isNaN(d.getTime())) return '';
  try { return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }); }
  catch { return d.toDateString(); }
};
/** A bare 'YYYY-MM-DD' (no time, no zone) → "13 Oct 2026". */
const ymdLabel = (v: any): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(str(v));
  if (!m) return str(v);
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  try { return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }); }
  catch { return str(v).slice(0, 10); }
};
const rupees = (minor: any) => String(Math.round(num(minor)) / 100);

const COLS = ['created_at', 'ref', 'name', 'email', 'department', 'techs_active', 'status', 'payment_status', 'received_minor', 'email_status'];
const LABELS: Record<string, string> = {
  created_at: 'When', ref: 'Ref', name: 'Name', email: 'Email', department: 'Department',
  techs_active: 'Technologies', status: 'Status', payment_status: 'Payment', received_minor: 'Received',
  email_status: 'E-mails',
};

type Filters = { q: string; status: string; payment: string; from: string; to: string };
const EMPTY: Filters = { q: '', status: '', payment: '', from: '', to: '' };

const INPUT_CLS = 'h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300 w-full';

const Field: React.FC<{ label: string; hint?: React.ReactNode; children: React.ReactNode }> = ({ label, hint, children }) => (
  <label className="flex flex-col gap-1 text-xs text-grayscale-1100 min-w-0">
    <span className="font-medium text-grayscale-1200">{label}</span>
    {children}
    {hint && <span className="text-[11px]">{hint}</span>}
  </label>
);

// ---------------------------------------------------------------------------
// Programme settings
// ---------------------------------------------------------------------------
/** Mirrors the server's ip_intel_programme._DOMAIN_RE / DOMAINS_MAX. */
const DOMAIN_RE = /^(?=.{3,120}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,24}$/;
const DOMAINS_MAX = 20;
const DEFAULT_DOMAIN = 'iitb.ac.in';

const domainList = (v: any): string[] =>
  (Array.isArray(v) ? v : []).map(x => str(x).trim().toLowerCase().replace(/^@/, '')).filter(Boolean);

/** The domains box (comma, semicolon, space or new line between them) → a clean list. */
const parseDomains = (text: string): string[] => {
  const out: string[] = [];
  text.split(/[\s,;]+/).forEach(x => {
    const s = x.trim().toLowerCase().replace(/^@/, '');
    if (s && !out.includes(s)) out.push(s);
  });
  return out;
};

interface Rule { domains: string[]; one: boolean; limited: boolean; cap: any; per: any }

/** "Open to @iitb.ac.in addresses · one form per address · 10 places in total · up to 10 per form"
 *  ("unlimited places, no count on the page" when places are not limited). */
const ruleLine = (v: Rule): string => [
  v.domains.length ? `Open to ${v.domains.map(x => `@${x}`).join(', ')} addresses` : 'Open to any e-mail address',
  v.one ? 'one form per address' : 'an address may register more than once',
  v.limited ? `${num(v.cap)} place${num(v.cap) === 1 ? '' : 's'} in total` : 'unlimited places, no count on the page',
  `up to ${num(v.per)} per form`,
].join(' · ');

/** The server's places_limited: missing (an older server) = limited. */
const limitedOf = (p: any): boolean => p?.places_limited !== false;

interface Draft {
  open: boolean; closes_on: string; places_limited: boolean; tech_cap: string; max_per_registration: string;
  share: string; full_price: string; tax_label: string; tax_rate: string; partner_email: string;
  who: 'any' | 'domains'; domains: string; one_per_address: boolean;
}

const draftOf = (p: any): Draft => {
  const domains = domainList(p?.allowed_email_domains);
  return {
    open: !!(p?.is_open ?? p?.open),
    closes_on: istDay(p?.closes_at),
    places_limited: limitedOf(p),
    tech_cap: str(p?.tech_cap ?? 10),
    max_per_registration: str(p?.max_per_registration ?? 10),
    share: rupees(p?.share_minor ?? 2000000),
    full_price: rupees(p?.full_price_minor ?? 4000000),
    tax_label: str(p?.tax_label ?? 'GST'),
    tax_rate: str(p?.tax_rate_pct ?? 18),
    partner_email: str(p?.partner_email),
    who: domains.length ? 'domains' : 'any',
    domains: domains.join(', '),
    one_per_address: p?.one_per_address !== false,
  };
};

/** What a draft would save, so switching back and forth is not a change.
 *  With places unlimited the cap is not sent, so an edit to it is not one. */
const normOf = (d: Draft) => ({
  ...d,
  tech_cap: d.places_limited ? d.tech_cap.trim() : '',
  max_per_registration: d.max_per_registration.trim(), tax_rate: d.tax_rate.trim(),
  partner_email: d.partner_email.trim(),
  domains: d.who === 'any' ? '' : parseDomains(d.domains).join(','),
});

const ProgrammeSettings: React.FC<{ programme: any; onSaved: () => void }> = ({ programme, onSaved }) => {
  const [d, setD] = useState<Draft>(() => draftOf(programme));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const base = useMemo(() => draftOf(programme), [programme]);
  useEffect(() => { setD(base); }, [base]);
  const dirty = JSON.stringify(normOf(d)) !== JSON.stringify(normOf(base));
  const set = (k: keyof Draft) => (v: string) => setD(x => ({ ...x, [k]: v }));

  const savedLimited = limitedOf(programme);
  const saved: Rule = {
    domains: domainList(programme?.allowed_email_domains), one: programme?.one_per_address !== false,
    limited: savedLimited, cap: programme?.tech_cap, per: programme?.max_per_registration,
  };
  const pending: Rule = {
    domains: d.who === 'any' ? [] : parseDomains(d.domains), one: d.one_per_address,
    limited: d.places_limited, cap: d.tech_cap, per: d.max_per_registration,
  };
  const nowLine = ruleLine(saved);
  const nextLine = ruleLine(pending);

  const chooseWho = (who: 'any' | 'domains') => setD(x => ({
    ...x, who, domains: who === 'domains' && !x.domains.trim() ? DEFAULT_DOMAIN : x.domains,
  }));

  const save = async () => {
    const share = Number(d.share), full = Number(d.full_price);
    if (!Number.isFinite(share) || !Number.isFinite(full) || share < 0 || full < 0) {
      setMsg({ kind: 'error', text: 'Prices must be amounts in rupees, e.g. 20000.' });
      return;
    }
    const rate = d.tax_rate.trim();
    if (!/^\d{1,3}(\.\d{1,2})?$/.test(rate) || Number(rate) > 100) {
      setMsg({ kind: 'error', text: `${d.tax_label || 'GST'} rate must be a percentage from 0 to 100, e.g. 18.` });
      return;
    }
    let domains: string[] = [];
    if (d.who === 'domains') {
      domains = parseDomains(d.domains);
      if (!domains.length) {
        setMsg({ kind: 'error', text: 'Add at least one domain, or choose “Any e-mail address”.' });
        return;
      }
      const bad = domains.find(x => !DOMAIN_RE.test(x));
      if (bad) { setMsg({ kind: 'error', text: `“${bad}” is not a domain (e.g. iitb.ac.in).` }); return; }
      if (domains.length > DOMAINS_MAX) { setMsg({ kind: 'error', text: `At most ${DOMAINS_MAX} domains.` }); return; }
    }
    setBusy(true); setMsg(null);
    const { error } = await panelPost(S, 'programme_save', {
      key: PROGRAMME,
      open: d.open,
      closes_at: d.closes_on,
      places_limited: d.places_limited,
      // Unlimited: the stored cap stays as it is, for when places are limited again.
      ...(d.places_limited ? { tech_cap: d.tech_cap.trim() } : {}),
      max_per_registration: d.max_per_registration.trim(),
      share_minor: Math.round(share * 100),
      full_price_minor: Math.round(full * 100),
      tax_label: d.tax_label,
      tax_rate_pct: Number(rate),
      partner_email: d.partner_email.trim(),
      allowed_email_domains: domains,
      one_per_address: d.one_per_address,
    });
    setBusy(false);
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    setMsg({ kind: 'ok', text: 'Saved. The page shows it straight away.' });
    onSaved();
  };

  const state = programme?.open
    ? (savedLimited
      ? `Open · ${num(programme?.remaining)} of ${num(programme?.tech_cap)} places left`
      : `Open · unlimited places · ${num(programme?.registered)} registered`)
    : programme?.closed_reason === 'full' ? 'Closed: every place is taken'
      : programme?.closed_reason === 'deadline' ? 'Closed: the closing date has passed'
        : 'Closed: switched off';
  const taxName = d.tax_label.trim() || 'GST';
  const registeredNow = num(programme?.registered);

  return (
    <Section title="Programme settings"
      right={
        <div className="flex items-center gap-2 flex-wrap">
          <Pill tone={programme?.open ? 'good' : 'neutral'}>{state}</Pill>
          <a href={PAGE_PATH} target="_blank" rel="noopener noreferrer" className="text-xs text-primary-700 underline">Open the page ↗</a>
        </div>
      }>
      <div className="rounded-lg border border-grayscale-300 bg-grayscale-200 px-3 py-2 flex flex-col gap-0.5 -mt-1">
        <div className="text-sm text-grayscale-1300"><span className="text-grayscale-1100">Rule now:</span> {nowLine}</div>
        {dirty && nextLine !== nowLine && (
          <div className="text-xs text-amber-700">After you save: {nextLine}</div>
        )}
      </div>
      <p className="text-xs text-grayscale-1100">
        What the page shows and accepts.{' '}
        {d.places_limited
          ? 'With no closing date, the page says registrations close once every place is taken.'
          : 'With places unlimited, the page shows no places count: only “No payment now.” and the closing date, if one is set.'}
        {' '}The IRCC e-mail is the page's contact address and is copied on every new-registration notice.
      </p>

      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-grayscale-1200">Who can register</span>
        <div className="flex items-center gap-4 flex-wrap text-sm text-grayscale-1300">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="ircc-who" checked={d.who === 'any'} onChange={() => chooseWho('any')} className="accent-primary-500" />
            Any e-mail address
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="ircc-who" checked={d.who === 'domains'} onChange={() => chooseWho('domains')} className="accent-primary-500" />
            Only these domains
          </label>
        </div>
        {d.who === 'domains' ? (
          <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
            <textarea value={d.domains} onChange={e => set('domains')(e.target.value)} rows={2} placeholder={DEFAULT_DOMAIN}
              aria-label="Allowed e-mail domains"
              className="w-full max-w-xl px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300" />
            <span className="text-[11px]">
              Comma or new line between them. Subdomains count: iitb.ac.in also lets in ee.iitb.ac.in.
              The form asks for an address at one of these.
            </span>
          </label>
        ) : (
          <span className="text-[11px] text-grayscale-1100">Any working address, e.g. a personal Gmail. The form shows no domain hint.</span>
        )}
        <label className="flex items-start gap-2 text-sm text-grayscale-1300 cursor-pointer mt-1">
          <input type="checkbox" checked={d.one_per_address} className="mt-0.5"
            onChange={e => setD(x => ({ ...x, one_per_address: e.target.checked }))} />
          <span>
            One registration per e-mail address
            <span className="ml-2 text-[11px] text-grayscale-1100">Untick to let the same person send the form more than once.</span>
            <span className="block text-[11px] text-grayscale-1100">
              {d.one_per_address
                ? 'An address that has registered cannot send the form again (cancelled ones do not count).'
                : d.places_limited
                  ? 'The same address can send the form again; the places in total and the per-form limit still apply.'
                  : 'The same address can send the form again; the per-form limit still applies.'}
            </span>
          </span>
        </label>
      </div>

      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}>
        <Field label="Registrations" hint="Switch off to close the form now">
          <span className="h-9 flex items-center gap-2 text-sm text-grayscale-1300">
            <input type="checkbox" checked={d.open} onChange={e => setD(x => ({ ...x, open: e.target.checked }))} />
            {d.open ? 'Open' : 'Closed'}
          </span>
        </Field>
        <Field label="Closing date (India time)"
          hint={d.closes_on ? 'Open to the end of that day' : d.places_limited ? 'No date: closes when full' : 'No date: open until you switch it off'}>
          <span className="flex items-center gap-1">
            <input type="date" value={d.closes_on} onChange={e => set('closes_on')(e.target.value)} className={INPUT_CLS} />
            {d.closes_on && <Btn small onClick={() => set('closes_on')('')} title="No closing date">Clear</Btn>}
          </span>
        </Field>
        <Field label="Limit places"
          hint={d.places_limited ? 'The page shows the places left' : 'Unlimited — the page shows no places count'}>
          <span className="h-9 flex items-center gap-2 text-sm text-grayscale-1300">
            <input type="checkbox" checked={d.places_limited} onChange={e => setD(x => ({ ...x, places_limited: e.target.checked }))} />
            {d.places_limited ? 'Limited' : 'Unlimited'}
          </span>
        </Field>
        {d.places_limited && (
          <Field label="Places in total"
            hint={`Technologies across all registrations · ${registeredNow} registered now${savedLimited ? ` · ${num(programme?.remaining)} left` : ''}`}>
            <input type="number" min={1} value={d.tech_cap} onChange={e => set('tech_cap')(e.target.value)} className={INPUT_CLS} />
          </Field>
        )}
        <Field label="Most technologies in one form" hint="The form's + button stops here">
          <input type="number" min={1} max={50} value={d.max_per_registration}
            onChange={e => set('max_per_registration')(e.target.value)} className={INPUT_CLS} />
        </Field>
        <Field label="Faculty share (₹)" hint="Per technology, before tax">
          <input type="number" min={0} value={d.share} onChange={e => set('share')(e.target.value)} className={INPUT_CLS} />
        </Field>
        <Field label="Full price (₹)" hint="Per technology, before tax">
          <input type="number" min={0} value={d.full_price} onChange={e => set('full_price')(e.target.value)} className={INPUT_CLS} />
        </Field>
        <Field label="Tax label">
          <input type="text" value={d.tax_label} maxLength={20} onChange={e => set('tax_label')(e.target.value)} className={INPUT_CLS} />
        </Field>
        <Field label={`${taxName} rate (%)`} hint={`Only for the amount incl. ${taxName} under Payments`}>
          <input type="number" min={0} max={100} step="0.01" value={d.tax_rate} onChange={e => set('tax_rate')(e.target.value)} className={INPUT_CLS} />
        </Field>
        <Field label="IRCC e-mail">
          <input type="email" value={d.partner_email} placeholder="Not set: info@ is shown"
            onChange={e => set('partner_email')(e.target.value)} className={INPUT_CLS} />
        </Field>
      </div>
      {d.places_limited && d.open && /^\d+$/.test(d.tech_cap.trim()) && registeredNow >= num(d.tech_cap.trim()) && (
        <div className="text-xs text-amber-700">
          {registeredNow} technologies are registered already, so with {num(d.tech_cap.trim())} places in total the form closes as full.
        </div>
      )}
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      <div className="flex items-center gap-2 flex-wrap">
        <Btn kind="primary" small disabled={!dirty || busy} onClick={save}>{busy ? 'Saving…' : 'Save settings'}</Btn>
        {dirty && <Btn small onClick={() => { setD(base); setMsg(null); }} disabled={busy}>Undo changes</Btn>}
        {dirty && <span className="text-[11px] text-amber-700">unsaved changes</span>}
        {programme?.updated_at && (
          <span className="text-[11px] text-grayscale-1100">
            last change {fmtDate(programme.updated_at)}{programme.updated_by ? ` by ${programme.updated_by}` : ''}
          </span>
        )}
      </div>
    </Section>
  );
};

// ---------------------------------------------------------------------------
// One technology inside the drawer: title, TRL, listing match, cancel.
// ---------------------------------------------------------------------------
const TechRow: React.FC<{ tech: any; regCancelled: boolean; onSaved: (row: any, warning: string) => void }> =
  ({ tech, regCancelled, onSaved }) => {
  const [title, setTitle] = useState(str(tech.title));
  const [trl, setTrl] = useState(str(tech.trl));
  const [match, setMatch] = useState('');
  const [hits, setHits] = useState<any[]>([]);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  useEffect(() => { setTitle(str(tech.title)); setTrl(str(tech.trl)); }, [tech.title, tech.trl]);
  const cancelled = str(tech.status) === 'cancelled';
  const dirty = title.trim() !== str(tech.title) || trl !== str(tech.trl);

  const post = async (body: Record<string, any>, what: string) => {
    setBusy(what); setErr('');
    const { data, error } = await panelPost(S, 'registration_tech_update', { tech_id: tech.id, ...body });
    setBusy('');
    if (error) { setErr(error); return false; }
    onSaved(data?.row, str(data?.warning));
    return true;
  };

  const search = async () => {
    const q = match.trim();
    if (q.length < 2) return;
    // A pasted UTECH id or Cosmos link is matched directly.
    if (/UTECH-|^https?:\/\//i.test(q)) {
      if (await post({ utech_technology_id: q }, 'match')) { setMatch(''); setHits([]); }
      return;
    }
    setBusy('search'); setErr('');
    const { data, error } = await panelGet(S, 'registration_tech_search', { q });
    setBusy('');
    if (error) { setErr(error); return; }
    const list = rowsOf(data, 'results');
    setHits(list);
    if (!list.length) setErr('No Cosmos listing matches that title.');
  };

  return (
    <div className={`rounded-lg border p-3 flex flex-col gap-2 ${cancelled ? 'border-grayscale-300 bg-grayscale-200 opacity-75' : 'border-grayscale-300 bg-white'}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-[11px] uppercase tracking-wide text-grayscale-1100 font-bold">Technology {tech.n}</span>
        <span className="flex items-center gap-2">
          {cancelled ? <Pill tone="neutral">Cancelled</Pill> : str(tech.status) === 'ordered' ? <Pill tone="good">Ordered</Pill> : <Pill tone="info">Registered</Pill>}
          {str(tech.status) !== 'ordered' && (
            <Btn small onClick={() => post({ status: cancelled ? 'registered' : 'cancelled' }, 'status')} disabled={!!busy}
              title={cancelled ? 'Count this technology again' : 'Withdraw this technology; its place is freed'}>
              {busy === 'status' ? '…' : cancelled ? 'Restore' : 'Cancel this one'}
            </Btn>
          )}
        </span>
      </div>
      <div className="flex gap-2 flex-wrap items-start">
        <textarea value={title} onChange={e => setTitle(e.target.value)} rows={2} maxLength={300}
          className="flex-1 min-w-[14rem] px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm" />
        <Select value={trl} onChange={setTrl} options={TRLS} labels={Object.fromEntries(TRLS.map(t => [t, `TRL ${t}`]))} />
        {dirty && (
          <Btn small kind="primary" onClick={() => post({ title: title.trim(), trl }, 'save')} disabled={!!busy}>
            {busy === 'save' ? 'Saving…' : 'Save'}
          </Btn>
        )}
      </div>
      <div className="text-xs text-grayscale-1200">
        IRCC / Cosmos listing:{' '}
        {tech.utech_technology_id ? (
          <span className="inline-flex items-center gap-2 flex-wrap">
            <span className="font-mono">{tech.utech_technology_id}</span>
            <span className="text-grayscale-1100">{[tech.listing_title, tech.listing_university].filter(Boolean).join(' · ')}</span>
            <Btn small onClick={() => post({ utech_technology_id: '' }, 'clear')} disabled={!!busy}>Clear</Btn>
          </span>
        ) : <span className="text-grayscale-1100">not matched yet</span>}
      </div>
      <div className="flex gap-2 flex-wrap items-center">
        <input value={match} onChange={e => setMatch(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void search(); } }}
          placeholder="Search a title, or paste a UTECH id / Cosmos link"
          className="h-8 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-xs flex-1 min-w-[14rem]" />
        <Btn small onClick={search} disabled={!!busy || match.trim().length < 2}>
          {busy === 'search' || busy === 'match' ? '…' : /UTECH-|^https?:\/\//i.test(match.trim()) ? 'Match' : 'Search'}
        </Btn>
      </div>
      {hits.length > 0 && (
        <div className="flex flex-col gap-1">
          {hits.slice(0, 8).map(h => (
            <button key={str(h.utech_technology_id)} type="button"
              onClick={async () => { if (await post({ utech_technology_id: h.utech_technology_id }, 'match')) { setHits([]); setMatch(''); } }}
              className="text-left text-xs rounded-md border border-grayscale-300 px-2 py-1 hover:bg-primary-50">
              <span className="font-mono">{str(h.utech_technology_id)}</span> · {str(h.title)}
              {h.university_name ? <span className="text-grayscale-1100"> · {str(h.university_name)}</span> : null}
            </button>
          ))}
        </div>
      )}
      {err && <div className="text-[11px] text-red-600">{err}</div>}
      {regCancelled && !cancelled && <div className="text-[11px] text-grayscale-1100">The registration is cancelled, so this does not hold a place.</div>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Payment section inside the drawer: what is expected, what is in, the
// entries, "Record payment" and a status override. Every action answers with
// the registration, which replaces the drawer's copy.
// ---------------------------------------------------------------------------
const PaymentSection: React.FC<{
  r: any; taxRate: number; loading: boolean; loadErr: string; onRetry: () => void;
  onApplied: (next: any | null) => void;
}> = ({ r, taxRate, loading, loadErr, onRetry, onApplied }) => {
  const cur = str(r.currency) || 'INR';
  const taxLabel = str(r.tax_label) || 'GST';
  const cancelled = str(r.status) === 'cancelled';
  const has = (v: any) => v !== undefined && v !== null && v !== '';
  const rate = has(r.tax_rate_pct) && Number.isFinite(Number(r.tax_rate_pct)) ? Number(r.tax_rate_pct) : taxRate;
  const live = num(r.techs_active);
  // The server sends these; the fallbacks only cover a payload from before payments.
  const expected = has(r.expected_minor) ? num(r.expected_minor) : cancelled ? 0 : num(r.share_minor) * live;
  const expectedTax = has(r.expected_with_tax_minor) ? num(r.expected_with_tax_minor) : Math.round(expected * (1 + rate / 100));
  const payments: any[] = Array.isArray(r.payments) ? r.payments : [];
  // received = the money as typed; receivedEx = the same entries before
  // GST, which is what is compared with the (before-GST) expected amount.
  const received = has(r.received_minor) ? num(r.received_minor) : payments.reduce((a, p) => a + num(p.amount_minor), 0);
  // Each entry at the rate it was recorded at (the server stores it).
  const receivedEx = has(r.received_ex_tax_minor) ? num(r.received_ex_tax_minor)
    : payments.reduce((a, p) => a + exTax(num(p.amount_minor), !!p.includes_tax,
      has(p.tax_rate_pct) ? Number(p.tax_rate_pct) : rate), 0);
  const gap = Math.max(0, expected - receivedEx);
  const settled = ['received', 'waived'].includes(str(r.payment_status));
  const outstanding = has(r.outstanding_minor) ? num(r.outstanding_minor)
    : settled || (gap <= PAID_SLACK_MINOR && receivedEx > 0) ? 0 : gap;
  const outstandingTax = has(r.outstanding_with_tax_minor) ? num(r.outstanding_with_tax_minor)
    : Math.round(outstanding * (1 + rate / 100));
  const status = (str(r.payment_status) || 'pending') as PayStatus;

  const [amount, setAmount] = useState('');
  const [amountTouched, setAmountTouched] = useState(false);
  const [incl, setIncl] = useState(false);
  const [on, setOn] = useState(todayIst());
  const [method, setMethod] = useState(METHODS[0]);
  const [reference, setReference] = useState('');
  const [pnote, setPnote] = useState('');
  const [mark, setMark] = useState<'' | 'received' | 'partial'>('');
  const [busy, setBusy] = useState('');
  const [formErr, setFormErr] = useState('');
  const [listErr, setListErr] = useState('');
  const [ovErr, setOvErr] = useState('');
  const [ok, setOk] = useState('');
  const [ovStatus, setOvStatus] = useState<string>(status);
  const [ovNote, setOvNote] = useState(str(r.payment_note));

  // The amount follows what is still owed until someone types over it.
  const prefill = status === 'waived' ? 0 : incl ? outstandingTax : outstanding;
  useEffect(() => { if (!amountTouched) setAmount(prefill > 0 ? rupees(prefill) : ''); }, [prefill, amountTouched]);
  useEffect(() => { setOvStatus(str(r.payment_status) || 'pending'); setOvNote(str(r.payment_note)); }, [r.payment_status, r.payment_note]);

  const amt = parseAmount(amount);
  const amtOk = Number.isFinite(amt) && amt > 0;
  const autoMark: 'received' | 'partial' =
    receivedEx + (amtOk ? exTax(amt, incl, rate) : 0) >= expected - PAID_SLACK_MINOR ? 'received' : 'partial';
  const markAs = mark || autoMark;
  const today = todayIst();

  const record = async () => {
    if (busy) return;
    setFormErr(''); setOk('');
    if (!amtOk) { setFormErr('Enter the amount in rupees, e.g. 20,000.'); return; }
    if (amt > AMOUNT_MAX_MINOR) { setFormErr('That is more than ₹1 crore. Check the amount.'); return; }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(on)) { setFormErr('Pick the date it was received.'); return; }
    if (today && on > today) { setFormErr('The date cannot be in the future.'); return; }
    if (on < EARLIEST_DAY) { setFormErr('That date looks wrong: it is before 2020.'); return; }
    setBusy('add');
    const { data, error } = await panelPost(S, 'registration_payment_add', {
      id: r.id, amount: amt / 100, received_on: on, method,
      reference: reference.trim(), note: pnote.trim(), includes_tax: incl, status: markAs,
    });
    setBusy('');
    if (error) { setFormErr(error); return; }
    const next = regOf(data);
    onApplied(next);
    const st = str(next?.payment_status) || markAs;
    setOk(`Recorded ${money(amt, cur)}${incl ? ` incl. ${taxLabel}` : ''} received on ${ymdLabel(on)}. Payment is now ${(PAY_META[st]?.label || st).toLowerCase()}.`);
    setAmountTouched(false); setIncl(false); setOn(todayIst()); setReference(''); setPnote(''); setMark('');
  };

  const remove = async (p: any) => {
    if (busy) return;
    setListErr(''); setOk('');
    setBusy(`del:${str(p.id)}`);
    const { data, error } = await panelPost(S, 'registration_payment_delete', { id: r.id, payment_id: p.id });
    setBusy('');
    if (error) { setListErr(error); return; }
    const next = regOf(data);
    onApplied(next);
    const st = str(next?.payment_status);
    setOk(`Deleted the ${inr(p.amount_minor, cur)} entry of ${ymdLabel(p.received_on)}.${st ? ` Payment is now ${(PAY_META[st]?.label || st).toLowerCase()}.` : ''}`);
  };

  const override = async () => {
    if (busy) return;
    setOvErr(''); setOk('');
    setBusy('status');
    const { data, error } = await panelPost(S, 'registration_payment_status', { id: r.id, status: ovStatus, note: ovNote.trim() });
    setBusy('');
    if (error) { setOvErr(error); return; }
    onApplied(regOf(data));
    setOk(`Payment marked ${(PAY_META[ovStatus]?.label || ovStatus).toLowerCase()}.`);
  };
  const ovDirty = ovStatus !== status || ovNote.trim() !== str(r.payment_note).trim();

  return (
    <Section title="Payment" right={<PayPill row={r} />}>
      <p className="text-[11px] text-grayscale-1100 -mt-1">
        Recorded by hand when IRCC (or the faculty member) pays. Nothing is e-mailed.
      </p>
      {ok && <Msg kind="ok">{ok}</Msg>}
      <div className="grid grid-cols-2 gap-2">
        <Fact label={`Expected (+${taxLabel})`} value={cancelled ? 'Nothing (cancelled)' : `${inr(expected, cur)} + ${taxLabel}`}
          title={`${live} ${live === 1 ? 'technology' : 'technologies'} × ${inr(r.share_minor, cur)}`} />
        <Fact label={`Expected incl. ${taxLabel} (${rate}%)`} value={inr(expectedTax, cur)} />
        <Fact label="Received so far" value={
          <span>{inr(received, cur)}{payments.length ? <span className="text-xs font-normal text-grayscale-1100"> · {payments.length} {payments.length === 1 ? 'entry' : 'entries'}</span> : null}
            {receivedEx !== received ? <span className="block text-xs font-normal text-grayscale-1100">{inr(receivedEx, cur)} before {taxLabel}</span> : null}</span>} />
        <Fact label="Outstanding" value={
          status === 'waived' ? 'Nothing (waived)'
            : outstanding > 0 ? <span>{inr(outstanding, cur)} + {taxLabel}<span className="block text-xs font-normal text-grayscale-1100">{inr(outstandingTax, cur)} incl. {taxLabel}</span></span>
              : receivedEx > expected + PAID_SLACK_MINOR ? <span>Nothing<span className="block text-xs font-normal text-grayscale-1100">{inr(receivedEx - expected, cur)} over the amount before {taxLabel}</span></span>
                : 'Nothing'} />
      </div>
      {(r.payment_note || r.payment_updated_at) && (
        <div className="text-[11px] text-grayscale-1100">
          {r.payment_note ? <><b>Payment note:</b> {str(r.payment_note)} </> : null}
          {r.payment_updated_at ? <>(status set {fmtDate(r.payment_updated_at)}{r.payment_updated_by ? ` by ${r.payment_updated_by}` : ''})</> : null}
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <span className="text-[11px] uppercase tracking-wide text-grayscale-1100 font-bold">Entries</span>
        {loadErr && !payments.length ? (
          <div className="text-[11px] text-red-600 flex items-center gap-2 flex-wrap">
            Could not load the entries: {loadErr} <Btn small onClick={onRetry}>Try again</Btn>
          </div>
        ) : loading && !payments.length ? (
          <div className="text-xs text-grayscale-1100">Loading…</div>
        ) : !payments.length ? (
          <div className="text-xs text-grayscale-1100">No payments recorded yet.</div>
        ) : payments.map(p => (
          <div key={str(p.id)} className="rounded-lg border border-grayscale-300 px-3 py-2 flex items-start justify-between gap-3">
            <div className="min-w-0 flex flex-col gap-0.5">
              <div className="flex items-center gap-2 flex-wrap text-sm text-grayscale-1300">
                <b>{inr(p.amount_minor, cur)}</b>
                {p.includes_tax ? <Pill tone="info">incl. {taxLabel}</Pill> : null}
                <span className="text-xs text-grayscale-1200">{ymdLabel(p.received_on)} · {str(p.method) || '—'}</span>
                {p.reference ? <span className="font-mono text-xs text-grayscale-1200 break-all">{str(p.reference)}</span> : null}
              </div>
              {p.note ? <div className="text-xs text-grayscale-1200 break-words">{str(p.note)}</div> : null}
              <div className="text-[11px] text-grayscale-1100">
                recorded {fmtDate(p.recorded_at)}{p.recorded_by ? ` by ${str(p.recorded_by)}` : ''}
              </div>
            </div>
            {busy === `del:${str(p.id)}`
              ? <span className="text-xs text-grayscale-1100 whitespace-nowrap">Deleting…</span>
              : <DangerBtn small onConfirm={() => { void remove(p); }}>Delete</DangerBtn>}
          </div>
        ))}
        {listErr && <div className="text-[11px] text-red-600">{listErr}</div>}
      </div>

      <div className="rounded-lg border border-primary-300 bg-primary-100 p-3 flex flex-col gap-2">
        <span className="text-xs font-bold text-grayscale-1300">Record payment</span>
        <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
          <Field label="Amount (₹)"
            hint={status === 'waived' ? 'Waived: nothing is owed'
              : prefill > 0 ? `Still owed: ${inr(prefill, cur)}${incl ? ` incl. ${taxLabel}` : ` + ${taxLabel}`}` : 'Nothing is owed'}>
            <input type="text" inputMode="decimal" value={amount} placeholder="e.g. 20,000" disabled={busy === 'add'}
              onChange={e => { setAmount(e.target.value); setAmountTouched(true); setFormErr(''); }} className={INPUT_CLS} />
          </Field>
          <Field label="Received on">
            <input type="date" value={on} min={EARLIEST_DAY} max={today || undefined} disabled={busy === 'add'}
              onChange={e => { setOn(e.target.value); setFormErr(''); }} className={INPUT_CLS} />
          </Field>
          <Field label="Method">
            <select value={method} onChange={e => setMethod(e.target.value)} disabled={busy === 'add'}
              className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm w-full">
              {METHODS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </Field>
          <Field label="Reference">
            <input type="text" value={reference} maxLength={REFERENCE_MAX} placeholder="UTR, cheque no.…" disabled={busy === 'add'}
              onChange={e => setReference(e.target.value)} className={INPUT_CLS} />
          </Field>
        </div>
        <label className="flex items-center gap-2 text-sm text-grayscale-1300 cursor-pointer w-fit">
          <input type="checkbox" checked={incl} disabled={busy === 'add'} onChange={e => setIncl(e.target.checked)} />
          Amount includes {taxLabel}
        </label>
        <textarea value={pnote} onChange={e => setPnote(e.target.value)} rows={2} maxLength={PAY_NOTE_MAX} disabled={busy === 'add'}
          placeholder="Note (optional), e.g. IRCC paid for both technologies"
          className="w-full px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm" />
        <div className="flex items-center gap-4 flex-wrap text-sm text-grayscale-1300">
          <span className="text-xs text-grayscale-1100">Mark as</span>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name={`ircc-mark-${str(r.id)}`} checked={markAs === 'received'} disabled={busy === 'add'}
              onChange={() => setMark('received')} className="accent-primary-500" />
            Received in full
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name={`ircc-mark-${str(r.id)}`} checked={markAs === 'partial'} disabled={busy === 'add'}
              onChange={() => setMark('partial')} className="accent-primary-500" />
            Part payment
          </label>
          {mark && mark !== autoMark
            ? <button type="button" onClick={() => setMark('')} className="text-[11px] text-primary-700 underline">go by the amount</button>
            : <span className="text-[11px] text-grayscale-1100">set by the amount</span>}
        </div>
        {formErr && <div className="text-[11px] text-red-600" role="alert">{formErr}</div>}
        <div className="flex items-center gap-2 flex-wrap">
          <Btn kind="primary" small onClick={record} disabled={!!busy || !amount.trim()}>
            {busy === 'add' ? 'Recording…' : 'Record payment'}
          </Btn>
          {amtOk && <span className="text-[11px] text-grayscale-1100">{money(amt, cur)}{incl ? ` incl. ${taxLabel}` : ''} · {method}</span>}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="text-[11px] uppercase tracking-wide text-grayscale-1100 font-bold">Set the status by hand</span>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={ovStatus} onChange={setOvStatus} options={[...PAY_STATUSES]}
            labels={Object.fromEntries(PAY_STATUSES.map(s => [s, PAY_META[s].label]))} />
          <input value={ovNote} onChange={e => setOvNote(e.target.value)} maxLength={PAY_NOTE_MAX} disabled={busy === 'status'}
            placeholder="Note (optional), e.g. waived by IRCC"
            className="h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm flex-1 min-w-[12rem]" />
          <Btn small onClick={override} disabled={!!busy || !ovDirty}>{busy === 'status' ? 'Saving…' : 'Set status'}</Btn>
        </div>
        <span className="text-[11px] text-grayscale-1100">
          For a waiver, or when IRCC confirms payment without an amount. Recording or deleting an entry sets it again.
        </span>
        {ovErr && <div className="text-[11px] text-red-600">{ovErr}</div>}
      </div>
    </Section>
  );
};

// ---------------------------------------------------------------------------
// Drawer
// ---------------------------------------------------------------------------
const RegistrationDrawer: React.FC<{ row: any; partnerEmail: string; taxRate: number; onClose: () => void; onChanged: () => void }> =
  ({ row, partnerEmail, taxRate, onClose, onChanged }) => {
  const [r, setR] = useState<any>(row);
  const [status, setStatus] = useState(str(row.status) || 'new');
  const [note, setNote] = useState(str(row.admin_note));
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [armed, setArmed] = useState<'' | 'faculty' | 'team'>('');
  const [loaded, setLoaded] = useState(false);
  const [loadErr, setLoadErr] = useState('');
  const rRef = useRef<any>(row);
  rRef.current = r;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(''), 6000);
    return () => clearTimeout(t);
  }, [armed]);

  /** Folds a fresh copy of the registration in. The status and note drafts
   *  follow only when nobody has started editing them. */
  const merge = useCallback((next: any) => {
    if (!next || typeof next !== 'object') return;
    const oldStatus = str(rRef.current?.status) || 'new';
    const oldNote = str(rRef.current?.admin_note);
    setR((prev: any) => ({ ...prev, ...next }));
    setStatus(s => (s === oldStatus ? str(next.status) || 'new' : s));
    setNote(n => (n === oldNote ? str(next.admin_note) : n));
  }, []);

  // The list row has the totals; the payment entries come with the full record.
  const reload = useCallback(async () => {
    setLoadErr('');
    const { data, error } = await panelGet(S, 'registration', { id: row.id });
    if (error) { setLoadErr(error); return; }
    merge(regOf(data));
    setLoaded(true);
  }, [row.id, merge]);
  useEffect(() => { void reload(); }, [reload]);

  const apply = (next: any) => {
    if (!next || typeof next !== 'object') return;
    setR((prev: any) => ({ ...prev, ...next }));
    setStatus(str(next.status) || 'new');
    setNote(str(next.admin_note));
  };

  const save = async () => {
    setBusy('save'); setMsg(null);
    const { data, error } = await panelPost(S, 'registration_update', { id: r.id, status, admin_note: note });
    setBusy('');
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    apply(data?.row);
    setMsg(data?.warning ? { kind: 'error', text: `Saved. Note: ${data.warning}` } : { kind: 'ok', text: 'Saved.' });
    onChanged();
    void reload(); // cancelling or restoring changes what is expected
  };

  const resend = async (which: 'faculty' | 'team') => {
    if (armed !== which) { setArmed(which); return; }
    setArmed(''); setBusy(which); setMsg(null);
    const { data, error } = await panelPost(S, 'registration_resend', { id: r.id, which });
    setBusy('');
    if (error) { setMsg({ kind: 'error', text: `Resend failed: ${error}` }); return; }
    apply(data?.row);
    onChanged();
    const ok = data?.sent?.[which];
    setMsg(ok
      ? { kind: 'ok', text: which === 'faculty' ? `Confirmation re-sent to ${r.email}.` : `Notice re-sent to ${TEAM_INBOX}${partnerEmail ? ` with ${partnerEmail} in CC` : ''}.` }
      : { kind: 'error', text: `The e-mail did NOT go out: ${str(which === 'faculty' ? data?.row?.email_error : data?.row?.team_email_error) || 'see Email › Logs'}.` });
  };

  const onTechSaved = (row: any, warning: string) => {
    apply(row);
    setMsg(warning ? { kind: 'error', text: `Saved. Note: ${warning}` } : { kind: 'ok', text: 'Technology updated.' });
    onChanged();
    void reload(); // a cancelled technology is no longer expected to be paid for
  };

  const onPayment = (next: any | null) => {
    if (next) merge(next); else void reload();
    onChanged();
  };

  const dirty = status !== (str(r.status) || 'new') || note !== str(r.admin_note);
  const techs: any[] = Array.isArray(r.techs) ? r.techs : [];

  return (
    <div className="fixed inset-0 z-40 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative h-full bg-white shadow-xl flex flex-col" style={{ width: 'min(96vw, 46rem)' }}
        onClick={e => e.stopPropagation()} role="dialog" aria-label="IRCC registration">
        <div className="px-5 py-4 border-b border-grayscale-300 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-base font-bold text-grayscale-1300 font-grotesk font-mono">{r.ref || `#${r.id}`}</span>
              <RegPill status={r.status} />
              <PayPill row={r} />
            </div>
            <div className="text-xs text-grayscale-1100 truncate">{[r.name, r.email, r.department].filter(Boolean).join(' · ')}</div>
          </div>
          <Btn small onClick={onClose}>Close</Btn>
        </div>

        <div className="p-5 overflow-y-auto flex flex-col gap-4">
          {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}

          <Section title="Faculty member"
            right={<span className="text-xs text-grayscale-1100">{r.created_at ? fmtDate(r.created_at) : ''}</span>}>
            <div className="grid grid-cols-2 gap-2">
              <Fact label="Name" value={r.name} />
              <Fact label="Email" value={r.email ? <a href={`mailto:${r.email}`} className="text-primary-700 underline">{r.email}</a> : ''} />
              <Fact label="Department" value={r.department} />
              <Fact label="Share (frozen)" value={`${str(r.share_total_label)} for ${num(r.tech_count)}`} />
              <Fact label="Account" value={r.user_id ? <span className="font-mono text-xs">{r.user_id}</span> : 'Not signed in'} />
              <Fact label="Country" value={r.country} />
            </div>
            <div className="text-[11px] text-grayscale-1100">
              <b>They ticked:</b> “{str(r.consent_text)}”
            </div>
          </Section>

          <PaymentSection r={r} taxRate={taxRate} loading={!loaded} loadErr={loadErr} onRetry={() => { void reload(); }}
            onApplied={onPayment} />

          <Section title={`Technologies (${techs.filter(t => str(t.status) !== 'cancelled').length} active)`}>
            <p className="text-[11px] text-grayscale-1100 -mt-1">
              Match each one to its IRCC Tech Transfer / Cosmos listing, fix a title or TRL, or cancel one to free its place.
            </p>
            {techs.map(t => (
              <TechRow key={str(t.id)} tech={t} regCancelled={str(r.status) === 'cancelled'} onSaved={onTechSaved} />
            ))}
          </Section>

          <Section title="Status & note">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-grayscale-1100">Status</span>
              {/* "Payment received" is kept only for rows that already have it:
                  payment progress lives in the Payment section now. */}
              <Select value={status} onChange={setStatus}
                options={REG_STATUSES.filter(s => s !== 'payment_received' || str(r.status) === 'payment_received')}
                labels={Object.fromEntries(REG_STATUSES.map(s => [s, REG_META[s].label]))} />
              <span className="text-[11px] text-grayscale-1100">{REG_META[status]?.hint}</span>
            </div>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
              placeholder="Internal note, never sent (e.g. IRCC confirmed payment on 20 Oct)"
              className="w-full px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm" />
            <div className="flex items-center gap-2 flex-wrap">
              <Btn kind="primary" small disabled={!dirty || !!busy} onClick={save}>{busy === 'save' ? 'Saving…' : 'Save status & note'}</Btn>
              {dirty && <span className="text-[11px] text-amber-700">unsaved changes</span>}
              {r.updated_at && (
                <span className="text-[11px] text-grayscale-1100">
                  last change {fmtDate(r.updated_at)}{r.updated_by ? ` by ${r.updated_by}` : ''}
                </span>
              )}
            </div>
          </Section>

          <Section title="E-mails">
            <div className="grid grid-cols-2 gap-2">
              <Fact label="Confirmation to the faculty member" value={
                <span className="flex flex-col gap-0.5">
                  <MailPill status={r.email_status} error={r.email_error} what="Confirmation" />
                  {str(r.email_status) === 'failed' && r.email_error && <span className="text-[11px] text-red-600 font-normal">{str(r.email_error)}</span>}
                </span>} />
              <Fact label={`Notice to ${TEAM_INBOX}`} value={
                <span className="flex flex-col gap-0.5">
                  <MailPill status={r.team_email_status} error={r.team_email_error} what="Team notice" />
                  {r.team_cc && <span className="text-[11px] font-normal text-grayscale-1100">CC {str(r.team_cc)}</span>}
                  {str(r.team_email_status) === 'failed' && r.team_email_error && <span className="text-[11px] text-red-600 font-normal">{str(r.team_email_error)}</span>}
                </span>} />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Btn small kind={armed === 'faculty' ? 'primary' : 'subtle'} onClick={() => resend('faculty')} disabled={!!busy}>
                {busy === 'faculty' ? 'Sending…' : armed === 'faculty' ? `Confirm: e-mail ${r.email}` : 'Resend confirmation'}
              </Btn>
              <Btn small kind={armed === 'team' ? 'primary' : 'subtle'} onClick={() => resend('team')} disabled={!!busy}>
                {busy === 'team' ? 'Sending…' : armed === 'team' ? `Confirm: e-mail ${TEAM_INBOX}${partnerEmail ? ' + IRCC' : ''}` : 'Resend team notice'}
              </Btn>
            </div>
            <div className="text-[11px] text-grayscale-1100">
              The wording of both is under the <b>Emails</b> tab. A resend lists the technologies that are not cancelled.
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
const Registrations: React.FC = () => {
  const [f, setF] = useState<Filters>(EMPTY);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState('50');
  const [tick, setTick] = useState(0);
  const [dlErr, setDlErr] = useState('');
  const [dlBusy, setDlBusy] = useState<'' | 'csv' | 'xlsx'>('');
  const [open, setOpen] = useState<any | null>(null);
  const q = useDebounced(f.q);
  const set = (k: keyof Filters) => (v: string) => setF(s => ({ ...s, [k]: v }));

  const filterParams = useMemo(() => ({ q, status: f.status, payment: f.payment, from: f.from, to: f.to, programme: PROGRAMME }),
    [q, f.status, f.payment, f.from, f.to]);
  const filterKey = JSON.stringify(filterParams);
  useEffect(() => { setPage(0); }, [filterKey, perPage]);

  const res = useAdminGet(S, 'registrations', { ...filterParams, page, per_page: perPage }, [filterKey, page, perPage, tick]);
  const d = res.data || {};
  const rows = rowsOf(d, 'registrations');
  const per = Number(perPage) || 50;
  const { total, pages } = pageInfo(d, rows, per);
  const counts = d.counts || {};
  const k = d.kpis || {};
  const ps = d.payments_summary || null;
  const programme = d.programme || null;
  const cur = str(programme?.currency) || 'INR';
  const taxLabel = str(programme?.tax_label) || 'GST';

  const chips: { label: string; value: string; clear: () => void }[] = [];
  if (f.q) chips.push({ label: 'Search', value: f.q, clear: () => set('q')('') });
  if (f.status) chips.push({ label: 'Status', value: REG_META[f.status]?.label || f.status, clear: () => set('status')('') });
  if (f.payment) chips.push({ label: 'Payment', value: PAY_FILTER_LABELS[f.payment] || f.payment, clear: () => set('payment')('') });
  if (f.from) chips.push({ label: 'From', value: f.from, clear: () => set('from')('') });
  if (f.to) chips.push({ label: 'To', value: f.to, clear: () => set('to')('') });

  const download = async (format: 'csv' | 'xlsx') => {
    setDlBusy(format); setDlErr('');
    const params: Record<string, string> = {};
    Object.entries(filterParams).forEach(([key, v]) => { if (v) params[key] = String(v); });
    const { blob, filename, error } = await apiService.adminFetchFile(
      apiService.getStoredUserId() || '', format === 'csv' ? EXPORT_CSV : EXPORT_XLSX, params);
    setDlBusy('');
    if (error || !blob) { setDlErr(error || 'Download failed'); return; }
    saveBlob(blob, filename || `ip-intel-ircc-registrations.${format}`);
  };

  // kpis.places_limited (falling back to the programme's): false = unlimited places, cap / remaining null.
  const kpiLimited = k.places_limited !== undefined ? k.places_limited !== false : limitedOf(programme);
  const closesKpi = k.closes_at ? dayLabel(k.closes_at) : kpiLimited ? 'When full' : 'No date';

  return (
    <div className="flex flex-col gap-4">
      {res.data && (
        <KPIRow>
          <KPI label="Registrations" value={num(k.registrations).toLocaleString()} hint="not cancelled"
            onClick={() => setF(x => ({ ...x, status: '', payment: '' }))} active={!f.status && chips.length === 0} />
          <KPI label="New" value={num(k.new).toLocaleString()} hint={REG_META.new.hint}
            onClick={() => setF(x => ({ ...x, status: x.status === 'new' ? '' : 'new' }))} active={f.status === 'new'} />
          <KPI label="Technologies" value={kpiLimited ? `${num(k.technologies)} / ${num(k.cap)}` : num(k.technologies).toLocaleString()}
            hint={kpiLimited ? 'registered / places in total' : 'registered · no limit on places'} />
          <KPI label="Places left" value={kpiLimited ? num(k.remaining).toLocaleString() : 'Unlimited'}
            hint={k.open ? (kpiLimited ? 'open' : 'open · the page shows no count') : k.closed_reason === 'full' ? 'closed: full' : k.closed_reason === 'deadline' ? 'closed: date passed' : 'closed'} />
          <KPI label="Closes on" value={closesKpi} hint={k.closes_at ? 'end of day, India time' : 'no closing date set'} />
          {ps && (
            <KPI label="Received" value={inr(ps.received_minor, cur)}
              hint={`of ${inr(ps.expected_minor, cur)} expected (+${taxLabel}) · ${inr(ps.expected_with_tax_minor, cur)} incl. ${taxLabel}`} />
          )}
          {ps && (
            <KPI label="Payments"
              value={
                <span className="text-sm font-semibold leading-snug flex flex-wrap gap-x-1.5">
                  {([['Paid', ps.received_count], ['Part-paid', ps.partial_count], ['Pending', ps.pending_count], ['Waived', ps.waived_count]] as [string, any][])
                    .map(([label, n], i) => (
                      <span key={label} className="whitespace-nowrap">{i > 0 ? '· ' : ''}{label} {num(n)}</span>
                    ))}
                </span>}
              hint={f.payment === 'unpaid' ? 'showing the unpaid' : 'click to see the unpaid'}
              onClick={() => setF(x => ({ ...x, payment: x.payment === 'unpaid' ? '' : 'unpaid' }))} active={f.payment === 'unpaid'} />
          )}
        </KPIRow>
      )}

      {programme && <ProgrammeSettings programme={programme} onSaved={() => setTick(t => t + 1)} />}

      <Section title="IRCC registrations"
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-grayscale-1100">{total.toLocaleString()} registration{total === 1 ? '' : 's'}</span>
            {chips.length > 0 && <Btn small onClick={() => setF(EMPTY)}>Clear {chips.length} filter{chips.length === 1 ? '' : 's'}</Btn>}
            <Btn small onClick={() => setTick(t => t + 1)} disabled={res.loading}>Refresh</Btn>
            <Btn small onClick={() => download('csv')} disabled={!!dlBusy} title="One row per technology (same filters) as CSV">
              {dlBusy === 'csv' ? 'Preparing…' : 'CSV'}
            </Btn>
            <Btn small kind="primary" onClick={() => download('xlsx')} disabled={!!dlBusy}
              title="One row per technology (same filters) as an Excel workbook">
              {dlBusy === 'xlsx' ? 'Preparing…' : 'Download Excel'}
            </Btn>
          </div>
        }>
        <p className="text-xs text-grayscale-1100 -mt-1">
          From the “Register interest” form on <a href={PAGE_PATH} target="_blank" rel="noopener noreferrer" className="underline">{PAGE_PATH}</a>.
          Each one e-mails the faculty member a confirmation and {TEAM_INBOX} a notice
          {programme?.partner_email ? <> with <b>{programme.partner_email}</b> in CC</> : ' (IRCC is not copied until its e-mail is set above)'}.
          Nothing is paid on the site: IRCC collects the share, and you record what came in under Payment in each registration.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <TextInput value={f.q} onChange={set('q')} wide placeholder="Search name, email, department, technology, ref…" />
          <Select value={f.status} onChange={set('status')} options={['', ...REG_STATUSES]}
            labels={{ '': 'Any status', ...Object.fromEntries(REG_STATUSES.map(s => [s,
              `${REG_META[s].label}${counts[s] !== undefined ? ` (${num(counts[s])})` : ''}`])) }} />
          <Select value={f.payment} onChange={set('payment')} options={PAY_FILTERS} labels={PAY_FILTER_LABELS} />
          <span className="text-xs text-grayscale-1100">from</span>
          <TextInput type="date" value={f.from} onChange={set('from')} />
          <span className="text-xs text-grayscale-1100">to</span>
          <TextInput type="date" value={f.to} onChange={set('to')} />
        </div>
        {chips.length > 0 && (
          <div className="flex gap-1.5 flex-wrap">
            {chips.map(c => <Chip key={c.label} label={c.label} value={c.value} onClear={c.clear} />)}
          </div>
        )}
        {dlErr && <Msg kind="error">Export failed: {dlErr}</Msg>}
        {res.error && <Msg kind="error">{res.error}</Msg>}
        {res.loading && !res.data ? <Loading /> : (
          <DataTable rows={rows} columns={COLS} labels={LABELS} maxHeight="40rem"
            empty={chips.length ? 'No registrations match these filters.' : 'No registrations yet.'}
            render={{
              created_at: r => <span className="text-xs" title={str(r.created_at)}>{fmtDate(r.created_at)}</span>,
              ref: r => (
                <button type="button" onClick={() => setOpen(r)} title="Open this registration"
                  className="font-mono text-xs font-bold text-grayscale-1300 underline underline-offset-2">
                  {r.ref || `#${r.id}`}
                </button>
              ),
              name: r => <span className="font-medium">{r.name || '—'}</span>,
              email: r => r.email ? <a href={`mailto:${r.email}`} className="text-primary-700 underline">{r.email}</a> : '—',
              techs_active: r => (
                <span className="block max-w-[20rem] truncate text-xs" title={str(r.tech_titles)}>
                  <b>{num(r.techs_active)}</b>{num(r.techs_active) !== num(r.tech_count) ? ` of ${num(r.tech_count)}` : ''} · {str(r.tech_titles) || '—'}
                </span>
              ),
              status: r => <RegPill status={r.status} />,
              payment_status: r => <PayPill row={r} />,
              received_minor: r => {
                const got = num(r.received_minor), exp = num(r.expected_minor);
                return (
                  <span className="text-xs whitespace-nowrap"
                    title={`${inr(exp, r.currency)} expected + ${str(r.tax_label) || 'GST'} · ${inr(r.expected_with_tax_minor, r.currency)} incl.${r.last_received_on ? ` · last on ${ymdLabel(r.last_received_on)}` : ''}`}>
                    {got ? <b>{inr(got, r.currency)}</b> : '—'}
                    {exp > 0 && <span className="text-grayscale-1100"> of {inr(exp, r.currency)} + {str(r.tax_label) || 'GST'}</span>}
                  </span>
                );
              },
              email_status: r => (
                <span className="flex gap-1">
                  <MailPill status={r.email_status} error={r.email_error} what="Confirmation to the faculty member" />
                  <MailPill status={r.team_email_status} error={r.team_email_error} what="Notice to the team" />
                </span>
              ),
            }}
            actions={r => <Btn small onClick={() => setOpen(r)}>Open</Btn>} />
        )}
        {pages > 1 && (
          <Pager page={page} pages={pages} total={total} shown={rows.length} per={per}
            onPage={setPage} perPage={perPage} onPerPage={setPerPage} />
        )}
      </Section>

      {open && (
        <RegistrationDrawer key={str(open.id)} row={open} partnerEmail={str(programme?.partner_email)}
          taxRate={num(programme?.tax_rate_pct ?? 18)}
          onClose={() => setOpen(null)} onChanged={() => setTick(t => t + 1)} />
      )}
    </div>
  );
};

export default Registrations;
